import { Divider } from 'antd'
import { type ReactElement, useState } from 'react'
import styled from 'styled-components'
import { Button } from '../../general'
import Modal from '../../legacy/molecules/Modal'
import ImageSection from './ImageSection'

type PresentationCardProps = {
  contentRows?: string[]
  leftImage?: string | undefined
  rightImage?: string | undefined
  descriptionRows: string[]
  onCtaClick?: () => {}
}

interface RowProps {
  alignLeft?: boolean | '' | undefined
}

export function PresentationCard(props: PresentationCardProps): ReactElement {
  const { contentRows, leftImage, rightImage, descriptionRows, onCtaClick } =
    props
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)

  const contentRight = leftImage && !rightImage

  const handleRegisterClick = () => {
    setIsModalVisible(true)
    if (onCtaClick) {
      onCtaClick()
    }
  }

  return (
    <Wrapper>
      {contentRight && <ImageSection leftImage={leftImage} />}
      <DescriptionAndCta>
        {contentRows?.map((rowText: string) => (
          <ContentRow key={rowText} alignLeft={contentRight}>
            {rowText}
          </ContentRow>
        ))}
        <DescriptionContainer>
          {descriptionRows?.map((rowText: string) => (
            <DescriptionRow key={rowText} alignLeft={contentRight}>
              {rowText}
            </DescriptionRow>
          ))}
        </DescriptionContainer>
        <ButtonContainer alignLeft={contentRight}>
          <Button variant="primary" onClick={() => handleRegisterClick()}>
            Register
          </Button>
        </ButtonContainer>
      </DescriptionAndCta>
      {!contentRight && <ImageSection rightImage={rightImage} />}
      <Modal
        classicPadding
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        localisation={{
          title: <StyledTitle>Your interest has been Registered!</StyledTitle>,
          confirmText: 'Close'
        }}
        onCancel={() => setIsModalVisible(false)}
        onConfirm={() => setIsModalVisible(false)}
      >
        <StyledTopDivider />
        <StyledDescription>
          We will keep your information and notify you as soon as the Goals
          feature is released.
        </StyledDescription>
        <StyledMessage>Thanks from the LMC product team.</StyledMessage>
        <StyledBottomDivider />
      </Modal>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.color.neutral.color11};
  border-radius: 25px;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 36px 36px 16px;
  margin-bottom: 24px;

  ${({ theme }) => theme.mediaQueries.tabletDown} {
    padding-bottom: 36px;
  }

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 16px 16px 44px;
  }
`

const DescriptionAndCta = styled.div`
  margin-top: 20px;
  width: 35%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  font: ${({ theme }) => theme.font.family.mono};
  ${({ theme }) => theme.mediaQueries.tabletDown} {
    width: 100%;
  }
`

const StyledTopDivider = styled(Divider)`
  color: ${({ theme }) => theme.color.neutral.color8};
`
const StyledBottomDivider = styled(Divider)`
  color: ${({ theme }) => theme.color.neutral.color8};
  margin-bottom: 0;
`
const ContentRow = styled.div<RowProps>`
  line-height: 38px;
  font-size: 42px;
  text-align: ${({ alignLeft }) => (alignLeft ? 'left' : 'right')};
  color: ${({ theme }) => theme.color.category.healthRecords};
  font-weight: 700;
`

const DescriptionContainer = styled.div`
  font-size: 16px;
  margin-top: 44px;
  line-height: 24px;
  font-weight: 400;
  font: ${({ theme }) => theme.font.family.sansSerif};
`

const ButtonContainer = styled.div<RowProps>`
  margin-right: ${({ alignLeft }) => (alignLeft ? 'auto' : 0)};
  margin-left: ${({ alignLeft }) => (alignLeft ? 0 : 'auto')};
  margin-top: 8px;
`
const DescriptionRow = styled.div<RowProps>`
  font-size: 16px;
  text-align: right;
  text-align: ${({ alignLeft }) => (alignLeft ? 'left' : 'right')};
`

const StyledTitle = styled.div<RowProps>`
  font-size: 24px;
  font-weight: 500;
  line-height: 24px;
  // needed to overwrite default styling
  text-transform: none !important;
  color: ${({ theme }) => theme.color.neutral.color3};
`
const StyledDescription = styled.div<RowProps>`
  font-size: 18px;
  line-height: 24px;
  font-weight: 400;
  color: ${({ theme }) => theme.color.neutral.color3};
`
const StyledMessage = styled.div<RowProps>`
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
  margin-top: 16px;
  color: ${({ theme }) => theme.color.neutral.color3};
`
export default PresentationCard
